import React, { useMemo, useState } from 'react';
import { Building2, Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { SearchableSelect } from '@/shared/ui/custom/searchable-select';
import { formatUnitLabel } from '../types';
import type { UnitTourReportUnit } from './UnitTourReportDialog';

export type UnitItemsAction = 'report' | 'export';

const ALL_LAYOUTS_VALUE = '__all_layouts__';

export interface UnitItemsActionSelection {
  layoutId?: number;
  layoutName?: string;
  unit?: UnitTourReportUnit;
}

export interface UnitItemsActionLayout {
  id: number;
  name: string;
}

interface UnitItemsActionDialogProps {
  action: UnitItemsAction | null;
  layouts: UnitItemsActionLayout[];
  units: UnitTourReportUnit[];
  working: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    selection: UnitItemsActionSelection,
    action: UnitItemsAction,
  ) => Promise<void> | void;
}

const ACTION_COPY = {
  report: {
    title: 'Generate Report by Unit/Area',
    description: 'Choose a layout or level, then optionally narrow the report to a unit or area.',
    button: 'Generate Report',
    icon: FileText,
  },
  export: {
    title: 'Export by Unit/Area',
    description: 'Choose a layout or level, then optionally narrow the CSV to a unit or area.',
    button: 'Export CSV',
    icon: Download,
  },
} satisfies Record<
  UnitItemsAction,
  { title: string; description: string; button: string; icon: typeof FileText }
>;

export const UnitItemsActionDialog: React.FC<UnitItemsActionDialogProps> = ({
  action,
  layouts,
  units,
  working,
  onOpenChange,
  onConfirm,
}) => {
  const [selectedLayoutId, setSelectedLayoutId] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const layoutOptions = useMemo(
    () => [
      { value: ALL_LAYOUTS_VALUE, label: 'All layouts/levels' },
      ...layouts.map((layout) => ({
        value: String(layout.id),
        label: layout.name,
      })),
    ],
    [layouts],
  );
  const filteredUnits = useMemo(
    () => selectedLayoutId
      ? units.filter((unit) => String(unit.layout_id) === selectedLayoutId)
      : units,
    [selectedLayoutId, units],
  );
  const unitOptions = useMemo(
    () => filteredUnits.map((unit) => ({
      value: String(unit.id),
      label: formatUnitLabel(unit),
    })),
    [filteredUnits],
  );
  const selectedUnit = useMemo(
    () => filteredUnits.find((unit) => String(unit.id) === selectedUnitId),
    [filteredUnits, selectedUnitId],
  );
  const selectedLayout = useMemo(
    () => layouts.find((layout) => String(layout.id) === selectedLayoutId),
    [layouts, selectedLayoutId],
  );
  const selection = useMemo<UnitItemsActionSelection | null>(() => {
    if (selectedUnit) {
      return {
        layoutId: selectedUnit.layout_id,
        layoutName: selectedUnit.layout_name ?? undefined,
        unit: selectedUnit,
      };
    }
    if (selectedLayout) {
      return {
        layoutId: selectedLayout.id,
        layoutName: selectedLayout.name,
      };
    }
    return null;
  }, [selectedLayout, selectedUnit]);

  const handleOpenChange = (open: boolean) => {
    if (!open && working) return;
    if (!open) {
      setSelectedLayoutId('');
      setSelectedUnitId('');
    }
    onOpenChange(open);
  };

  const handleLayoutChange = (layoutId: string) => {
    setSelectedLayoutId(layoutId === ALL_LAYOUTS_VALUE ? '' : layoutId);
    setSelectedUnitId('');
  };

  if (!action) return null;

  const copy = ACTION_COPY[action];
  const ActionIcon = copy.icon;

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" strokeWidth={1.9} />
            {copy.title}
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Layout/Level
            </div>
            <SearchableSelect
              options={layoutOptions}
              value={selectedLayoutId}
              onValueChange={handleLayoutChange}
              placeholder="All layouts/levels"
              searchPlaceholder="Search layouts or levels..."
              emptyMessage="No layouts or levels found."
              disabled={working}
            />
          </div>

          <div className="space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Unit/Area
            </div>
            <SearchableSelect
              options={unitOptions}
              value={selectedUnitId}
              onValueChange={setSelectedUnitId}
              placeholder={
                selectedLayoutId
                  ? 'Optional — select a unit or area'
                  : 'Select a unit or area'
              }
              searchPlaceholder="Search units or areas..."
              emptyMessage={
                selectedLayoutId
                  ? 'No units or areas found for this layout/level.'
                  : 'No units or areas found.'
              }
              disabled={working}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={working}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => selection && void onConfirm(selection, action)}
            disabled={!selection || working}
            className="gap-2"
          >
            {working ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ActionIcon className="h-4 w-4" />
            )}
            {copy.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnitItemsActionDialog;
